/* @jsxImportSource @emotion/react */

import { useEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { useInView } from 'motion/react'
import { useIntl } from 'react-intl'

import { Section } from '../../../components/Section'
import { MEDIUM_BREAKPOINT } from '../../../utils/breakpoints'
import { AgentStage } from './AgentStage'
import { StepList } from './StepList'
import { getAgentSteps, STEP_DURATION_MS } from './steps'

const frameCss = css`
  display: flex;
  align-items: center;
  gap: 64px;

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    flex-direction: column;
    gap: 40px;
  }
`

const listColCss = css`
  flex: 1 1 45%;
  min-width: 0;

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    width: 100%;
    flex: none;
  }
`

export function SectionAgentHowItWorks() {
  const intl = useIntl()
  const steps = getAgentSteps(intl)
  const frameRef = useRef<HTMLDivElement>(null)
  const [activeStep, setActiveStep] = useState(0)
  // Bumped on manual selection so timer and progress bar restart on re-click.
  const [cycleKey, setCycleKey] = useState(0)
  const isInView = useInView(frameRef, { amount: 0.35 })
  // Starts on the first scroll into view, then keeps cycling.
  const [hasStarted, setHasStarted] = useState(false)

  useEffect(() => {
    if (!isInView || hasStarted) return
    setHasStarted(true)
    setCycleKey((key) => key + 1)
  }, [isInView, hasStarted])

  useEffect(() => {
    // Depending on cycleKey restarts the countdown on re-click.
    void cycleKey
    if (!hasStarted) return
    const timer = setTimeout(
      () => setActiveStep((activeStep + 1) % steps.length),
      STEP_DURATION_MS
    )
    return () => clearTimeout(timer)
  }, [hasStarted, activeStep, cycleKey, steps.length])

  const selectStep = (index: number) => {
    setActiveStep(index)
    setCycleKey((key) => key + 1)
  }

  return (
    <div className="overflow-hidden">
      <Section background="moon-dust-lighter" containerVerticalPadding={96}>
        <div ref={frameRef} css={frameCss}>
          <div css={listColCss}>
            <StepList
              steps={steps}
              activeStep={activeStep}
              cycleKey={cycleKey}
              stepDurationMs={STEP_DURATION_MS}
              onSelect={selectStep}
            />
          </div>
          {/* Remount on start so the choreography doesn't run offscreen. */}
          <AgentStage
            key={String(hasStarted)}
            step={steps[activeStep].id}
            surfaceColor={steps[activeStep].surfaceColor}
          />
        </div>
      </Section>
    </div>
  )
}
